<template>
<div class="filters">
  <div class="form-group row form-multiselect">
    <div class="col-md-12 filter-entities" v-show="hasEntityFilter">
      <select-entity :entities="entityAll"
                     :labelEntity="$t('Offers.filters.entities')"
                     colLabel="col-md-2"
                     colSelect="col-md-3"
                     marginEntity="m-t-5"
                     :preselectedEntityId="entityId"
                     @entity-changed="onEntityChange" />
    </div>
    <div class="col-md-12" v-if="showHubFilter">
      <select-hub ref="selectHubs"
                  :hubs="manageableHubs"
                  :selectedIds="hubIds"
                  @change="onHubChange" />
    </div>

    <div class="col-md-4 occupation-filter" v-if="isManager">
      <label for="replaced_occupation_id_in" class="control-label">
        {{ $t("Offers.filters.occupations") }}
      </label>
      <multiselect v-if="!isIE" v-model="replacedOccupations"
                   id="replaced_occupation_id_in"
                   label="name"
                   track-by="id"
                   @input="onOccupationsChange"
                   :placeholder="!needSelectFilter ? $t('multiselect.placeholder') : ''"
                   :showLabels="false"
                   :multiple="true"
                   :disabled="needSelectFilter || isLoading"
                   :options="occupationsOptions">
        <span slot="noResult">{{ $t('OfferForm.no_service') }}</span>
      </multiselect>
      <v-select v-else multiple v-model="replacedOccupations"
        :placeholder="$t('multiselect.placeholder')"
        :disabled="needSelectFilter || isLoading"
        :options="occupationsOptionsIE"
        @input="onOccupationsChangeIE"
        >
      </v-select>
    </div>

    <div class="col-md-4 service-filter" v-if="isManager">
      <label for="replaced_service_id_in" class="control-label">
        {{ $t("Offers.filters.services") }}
      </label>
      <multiselect v-if="!isIE" v-model="replacedServices"
                   id="replaced_service_id_in"
                   label="name"
                   track-by="id"
                   :placeholder="!needSelectFilter ? $t('multiselect.placeholder') : ''"
                   :showLabels="false"
                   :multiple="true"
                   :disabled="needSelectFilter || isLoading"
                   @input="onServicesChange"
                   :options="servicesOptions"
                   :group-label="showHubFilter ? 'hub' : ''"
                   :group-values="showHubFilter ? 'services' : ''"
                   :group-select="false">
        <span slot="noResult">{{ $t('OfferForm.no_occupation') }}</span>
      </multiselect>
      <v-select v-else multiple v-model="replacedServices"
        :placeholder="$t('multiselect.placeholder')"
        :options="servicesOptionsIE"
        @input="onServicesChangeIE"
        :disabled="needSelectFilter || isLoading"
        >
      </v-select>
    </div>

    <div class="col-md-4 publisher-filter" v-if="isManager">
      <label for="published_by_id_in" class="control-label">
        {{ $t("Offers.filters.published_by") }}
      </label>
      <multiselect v-if="!isIE" v-model="publishers"
                   id="published_by_id_in"
                   label="full_name"
                   track-by="id"
                   :placeholder="!needSelectFilter ? $t('multiselect.placeholder') : ''"
                   :showLabels="false"
                   :multiple="true"
                   :disabled="needSelectFilter || isLoading"
                   @input="onPublishersChange"
                   :options="coworkersOptions">
        <span slot="noResult">{{ $t('OfferForm.no_coworkers') }}</span>
      </multiselect>
      <v-select v-else multiple v-model="publishers"
        :placeholder="$t('multiselect.placeholder')"
        :disabled="needSelectFilter || isLoading"
        @input="onPublishersChangeIE"
        :options="coworkersOptionsIE"
        >
      </v-select>
    </div>
  </div>

  <div class="form-group row form-multiselect" v-if="isManager">
    <div class="col-md-4">
      <date-picker-filter v-model="startAt"
                   id="start_at_gteq"
                   :disabled="isLoading"
                   @input="changeFilters"
                   :beginningOfDay="true"
                   :disabledToday="disabledDate()"
                   labelText="Du" />
    </div>

    <div class="col-md-4">
      <date-picker-filter v-model="endAt"
                   id="start_at_lteq"
                   :disabled="isLoading"
                   @input="changeFilters"
                   :endOfDay="true"
                   :disabledToday="disabledDate()"
                   labelText="au" />
    </div>

    <div class="col-md-2">
      <button @click="clearFilters" class="btn btn-default btn-cons btn-block">
        {{ $t('Offers.filters.reset') }}
      </button>
    </div>

    <div class="col-md-2">
      <div class="dropdown" v-tooltip="exportIsLoading ? $t('Offers.filters.export_is_loading') : ''">
        <button class="btn btn-primary btn-cons btn-block"
                id="export-button"
                :disabled="exportIsLoading"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false">
          <i class="fa fa-cloud-download"></i>
          <span class="bold">{{ $t('Offers.filters.export') }}</span>
          <span class="caret"></span>
        </button>
       
        <ul class="dropdown-menu" aria-labelledby="export-button">
          <li class="dropdown-item">
            <button type="button" @click.prevent="triggerExport('csv')" :disabled="exportIsLoading">
              <span class="bold">CSV</span>
            </button>
          </li>
          <li class="dropdown-item">
            <button type="button" @click.prevent="triggerExport('xls')" :disabled="exportIsLoading">
              <span class="bold">XLS</span>
            </button>
          </li>
        </ul>
      </div>
    </div>

  </div>
</div>
</template>

<script type="text/javascript">
import { mapGetters, mapActions } from 'vuex'
import DatePickerFilter from '@/components/pages/DatePickerFilter'
import SelectEntity from '@/components/common/SelectEntity'
import SelectHub from '@/components/common/SelectHub'
import ie_mixin from '@/mixin/ie'
import AtInternetManager from '@/helpers/at_internet/manage_requests'

export default {
  data() {
    return {
      entityId: null,
      hubs: [],
      hubIds: [],
      replacedOccupations: [],
      replacedOccupationIds: [],
      replacedServices: [],
      replacedServiceIds: [],
      publishers: [],
      publisherIds: [],
      startAt: '',
      endAt: '',
      datepickerDefaultOptions: {
        startDate: new Date(2018, 1, 1)
      },
      disableSelect: true
    }
  },
  name: 'Filters',
  props: ['isManager', 'initFilters', 'isLoading', 'exportIsLoading'],
  mixins: [ie_mixin],
  components: { DatePickerFilter, SelectEntity, SelectHub },
  computed: {
    ...mapGetters({
      currentUser: 'auth/currentUser',
      manageableHubs: 'users/manageableHubs',
      manageableServices: 'users/manageableServices',
      occupationsOptions: 'users/manageableOccupations',
      coworkersOptions: 'users/coworkers',
      entitiesOptions: 'users/manageableEntities'
    }),
    coworkersOptionsIE() {
      if (this.coworkersOptions) {
        return this.coworkersOptions.map(item => item['full_name'])
      }
    },
    hasEntityFilter() {
      return this.entitiesOptions.length > 1
    },
    needSelectFilter() {
      return this.hasEntityFilter && this.disableSelect
    },
    hasManyHubs() {
      return this.manageableHubs.length > 1
    },
    showHubFilter() {
      return this.isManager && this.hasManyHubs && (!this.hasEntityFilter || this.entityId)
    },
    entityAll() {
      var entities = [{ id: null, name: this.$i18n.t('models.resource_types.all') }]
      return entities.concat(this.entitiesOptions)
    },
    servicesOptions() {
      if(!this.showHubFilter || this.isIE) {
        return this.manageableServices;
      }
      return this.buildServicesListGroupedByHubs();
    }
  },
  methods: {
    ...mapActions({
      getCurrentUser: 'auth/getUser',
      initManageableOptions: 'users/initManageableOptions',
      getCoworkers: 'users/getCoworkers'
    }),
    onOccupationsChange(occupations) {
      this.replacedOccupationIds = occupations.map(occupation => occupation.id)
      this.changeFilters()
    },
    onServicesChange(services) {
      this.replacedServiceIds = services.map(service => service.id)
      this.changeFilters()
    },
    onPublishersChange(publishers) {
      this.publisherIds = publishers.map(publisher => publisher.id)
      this.changeFilters()
    },
    onOccupationsChangeIE(occupation_names) {
      this.replacedOccupationIds = this.getIdsFromValues(occupation_names, this.occupationsOptions)
      this.changeFilters();
    },
    onServicesChangeIE(services_names) {
      this.replacedServiceIds = this.getIdsFromValues(services_names, this.servicesOptions)
      this.changeFilters();
    },
    onPublishersChangeIE(publisher_names) {
      this.publisherIds = this.getIdsFromValues(publisher_names, this.coworkersOptions, 'full_name')
      this.changeFilters();
    },
    onEntityChange(entity) {
      this.clearSelectFilters()
      this.entityId = entity.id
      this.hubIds = []
      this.disableSelect = (entity.id === null)

      if (!this.disableSelect) {
        this.initManageableOptions({ entityId: this.entityId });
        if (this.isManager) this.getCoworkers(this.entityId)
      }

      this.changeFilters()
    },
    onHubChange(hubs) {
      this.clearSelectFilters()
      this.hubIds = hubs.map(hub => hub.id)
      this.initManageableOptions({ entityId: this.entityId, hubIds: this.hubIds });
      if (this.isManager) this.getCoworkers(this.entityId)

      this.changeFilters()
    },
    clearSelectFilters: function() {
      this.replacedOccupations = []
      this.replacedServices = []
      this.publishers = []
      this.replacedOccupationIds = []
      this.replacedServiceIds = []
      this.publisherIds = []
    },
    clearFilters: function(){
      this.clearSelectFilters()
      this.startAt = ''
      this.endAt = ''
      this.entityId = null
      this.hubIds = []
      this.disableSelect = true

      this.changeFilters()
    },
    buildFilters() {
      return {
        entity_id_in: this.entityId,
        hub_id_in: this.hubIds,
        replaced_occupation_id_in: this.replacedOccupationIds,
        replaced_service_id_in: this.replacedServiceIds,
        published_by_id_in: this.publisherIds,
        start_at_gteq: this.startAt,
        start_at_lteq: this.endAt,
      }
    },
    async initializeFilters() {
      if (typeof this.initFilters !== 'object') return

      if ('entity_id_in' in this.initFilters) {
        this.entityId = this.initFilters.entity_id_in
        this.disableSelect = (this.entityId === null)
        await this.getCoworkers(this.entityId)
      }
      if ('hub_id_in' in this.initFilters) {
        this.hubIds = this.initFilters.hub_id_in
      }
      if ('replaced_occupation_id_in' in this.initFilters) {
        this.replacedOccupationIds = this.initFilters.replaced_occupation_id_in
        this.replacedOccupations = this.occupationsOptions.filter(occupation => {
          return this.initFilters.replaced_occupation_id_in.includes(occupation.id)
        })
      }
      if ('replaced_service_id_in' in this.initFilters) {
        this.replacedServiceIds = this.initFilters.replaced_service_id_in
        this.replacedServices = this.manageableServices.filter(service => {
          return this.initFilters.replaced_service_id_in.includes(service.id)
        })
      }
      if ('published_by_id_in' in this.initFilters && this.entityId) {
        this.publisherIds = this.initFilters.published_by_id_in
        this.publishers = this.coworkersOptions.filter(coworker => {
          return this.initFilters.published_by_id_in.includes(coworker.id)
        })
      }
      if ('start_at_gteq' in this.initFilters) {
        this.startAt = this.initFilters.start_at_gteq
      }
      if ('start_at_lteq' in this.initFilters) {
        this.endAt = this.initFilters.start_at_lteq
      }
    },
    changeFilters() {
      this.$emit('offersFiltersChanged', this.buildFilters());
    },
    exportClicked: function(){
      AtInternetManager.sendExportMyRequestsEvent();
      this.$emit('exportClicked')
    },
    triggerExport(format) {
      AtInternetManager.sendExportMyRequestsEvent();
      if (format === 'csv') {
        this.$emit('exportCSV');
      } else if (format === 'xls') {
        this.$emit('exportXLS');
      }
    },
    buildServicesListGroupedByHubs() {
      return this.manageableHubs.map(hub => {
        return {
          hub: hub.name,
          services: this.manageableServices.filter(service => service.hub_id == hub.id)
        };
      });
    },
    disabledDate() {
      if (this.$route.name === 'RequestHistories') {
        return true
      } else {
        return false
      }
    }
  },
  mounted: async function() {
    this.initializeFilters()
    await this.getCurrentUser()
    await this.initManageableOptions({ entityId: this.entityId, hubIds: this.hubIds });

    if (this.isManager && !this.hasEntityFilter) {
      await this.getCoworkers(this.entityId)
    }
  }
}

</script>

<style scoped type="text/css" lang="scss">
  .filters{
    margin-bottom: 20px;
  }

  .dropdown-wrapper {
    position: relative;
  }

  .dropdown-menu {
    padding: 0;
    right: 0;

    .dropdown-item {
      padding: 0;
      display: flex;
      margin-top: 4px;
    }

    button {
      flex: 1;
      border: 0;
      padding: 8px 0;
      line-height: 1.4;
      cursor: pointer;
      &:hover {
        background: #cccccc;
      }
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/offers/filters.vue